import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Sun, Moon, Coffee, Brain, Briefcase, Heart, Zap, Target,
    BookOpen, Clock, Plus, Check, X, SkipForward,
    Flame, Sparkles, Loader2, RefreshCw, Trash2,
    Calendar, CheckCircle2, Circle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useReplica } from '../contexts/ReplicaContext';
import { api } from '../lib/api';

// ============================================================================
// Types
// ============================================================================

interface RoutineStep {
    id: string;
    title: string;
    type: 'check' | 'workflow';
    workflowSlug?: string;
    description?: string;
    duration?: number;
}

interface StepResult {
    stepId: string;
    completed: boolean;
    completedAt?: string;
}

interface RoutineCompletion {
    id: string;
    status: string;
    stepsCompleted: number;
    stepsTotal: number;
    stepResults: StepResult[];
}

interface DailyRoutine {
    id: string;
    name: string;
    description: string;
    icon: string;
    color: string;
    frequency: string;
    timeSlot: string | null;
    duration: number | null;
    daysOfWeek: number[];
    steps: RoutineStep[];
    isEnabled: boolean;
    isProposed: boolean;
    createdBy: string;
    proposalNote: string | null;
    completionRate: number;
    lastCompletedAt: string | null;
    streakCount: number;
    completions: Array<{
        date: string;
        status: string;
        stepsCompleted: number;
        stepsTotal: number;
        stepResults: StepResult[];
    }>;
}

interface TodayPlanItem {
    routineId: string;
    name: string;
    description: string;
    icon: string;
    color: string;
    timeSlot: string | null;
    duration: number | null;
    steps: RoutineStep[];
    completion: RoutineCompletion | null;
    streakCount: number;
    completionRate: number;
}

// ============================================================================
// Constants
// ============================================================================

const ICON_MAP: Record<string, React.FC<any>> = {
    Sun, Moon, Coffee, Brain, Briefcase, Heart, Zap, Target, BookOpen, Clock,
};

const COLOR_MAP: Record<string, { bg: string; border: string; text: string; glow: string; gradient: string }> = {
    blue: { bg: 'rgba(59,130,246,0.08)', border: 'rgba(59,130,246,0.25)', text: '#3b82f6', glow: '0 0 20px rgba(59,130,246,0.15)', gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)' },
    purple: { bg: 'rgba(139,92,246,0.08)', border: 'rgba(139,92,246,0.25)', text: '#8b5cf6', glow: '0 0 20px rgba(139,92,246,0.15)', gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' },
    emerald: { bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.25)', text: '#10b981', glow: '0 0 20px rgba(16,185,129,0.15)', gradient: 'linear-gradient(135deg, #10b981, #059669)' },
    amber: { bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.25)', text: '#f59e0b', glow: '0 0 20px rgba(245,158,11,0.15)', gradient: 'linear-gradient(135deg, #f59e0b, #d97706)' },
    rose: { bg: 'rgba(244,63,94,0.08)', border: 'rgba(244,63,94,0.25)', text: '#f43f5e', glow: '0 0 20px rgba(244,63,94,0.15)', gradient: 'linear-gradient(135deg, #f43f5e, #e11d48)' },
    indigo: { bg: 'rgba(99,102,241,0.08)', border: 'rgba(99,102,241,0.25)', text: '#6366f1', glow: '0 0 20px rgba(99,102,241,0.15)', gradient: 'linear-gradient(135deg, #6366f1, #4f46e5)' },
    teal: { bg: 'rgba(20,184,166,0.08)', border: 'rgba(20,184,166,0.25)', text: '#14b8a6', glow: '0 0 20px rgba(20,184,166,0.15)', gradient: 'linear-gradient(135deg, #14b8a6, #0d9488)' },
    orange: { bg: 'rgba(249,115,22,0.08)', border: 'rgba(249,115,22,0.25)', text: '#f97316', glow: '0 0 20px rgba(249,115,22,0.15)', gradient: 'linear-gradient(135deg, #f97316, #ea580c)' },
};

const FREQ_LABELS: Record<string, string> = {
    daily: 'Every day',
    weekdays: 'Weekdays',
    weekends: 'Weekends',
    weekly: 'Weekly',
    custom: 'Custom',
};

// ============================================================================
// Component
// ============================================================================

interface DailyRoutinePageProps {
    onNavigate: (view: string) => void;
}

const DailyRoutinePage: React.FC<DailyRoutinePageProps> = ({ onNavigate }) => {
    const { user } = useAuth();
    const { userReplicas } = useReplica();
    const activeReplica = userReplicas?.[0];

    const [todayPlan, setTodayPlan] = useState<TodayPlanItem[]>([]);
    const [allRoutines, setAllRoutines] = useState<DailyRoutine[]>([]);
    const [proposals, setProposals] = useState<DailyRoutine[]>([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [togglingStep, setTogglingStep] = useState<string | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [generatePrompt, setGeneratePrompt] = useState('');
    const [activeTab, setActiveTab] = useState<'today' | 'all' | 'create'>('today');
    const [todayDate, setTodayDate] = useState('');

    // ── Fetch data ────────────────────────────────────────
    const fetchData = useCallback(async () => {
        if (!user || !activeReplica) return;
        setLoading(true);
        try {
            const [planRes, routinesRes, proposalsRes] = await Promise.all([
                api.get<{ plan: TodayPlanItem[]; date: string }>(`/routines/today?replicaId=${activeReplica.id}`),
                api.get<{ routines: DailyRoutine[] }>(`/routines?replicaId=${activeReplica.id}`),
                api.get<{ proposals: DailyRoutine[] }>(`/routines/proposals?replicaId=${activeReplica.id}`),
            ]);
            setTodayPlan(planRes.data?.plan || []);
            setTodayDate(planRes.data?.date || new Date().toISOString().split('T')[0]);
            setAllRoutines(routinesRes.data?.routines || []);
            setProposals(proposalsRes.data?.proposals || []);
        } catch (err) {
            console.error('Failed to fetch routines:', err);
        } finally {
            setLoading(false);
        }
    }, [user, activeReplica]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ── Actions ───────────────────────────────────────────
    const toggleStep = async (routineId: string, stepIndex: number) => {
        const key = `${routineId}-${stepIndex}`;
        setTogglingStep(key);
        try {
            await api.patch(`/routines/${routineId}/steps/${stepIndex}/toggle`);
            await fetchData();
        } catch (err) {
            console.error('Toggle failed:', err);
        } finally {
            setTogglingStep(null);
        }
    };

    const completeRoutine = async (routineId: string) => {
        try {
            await api.post(`/routines/${routineId}/complete`);
            await fetchData();
        } catch (err) {
            console.error('Complete failed:', err);
        }
    };

    const skipRoutine = async (routineId: string) => {
        try {
            await api.post(`/routines/${routineId}/skip`);
            await fetchData();
        } catch (err) {
            console.error('Skip failed:', err);
        }
    };

    const acceptProposal = async (routineId: string) => {
        try {
            await api.post(`/routines/proposals/${routineId}/accept`);
            await fetchData();
        } catch (err) {
            console.error('Accept failed:', err);
        }
    };

    const dismissProposal = async (routineId: string) => {
        try {
            await api.post(`/routines/proposals/${routineId}/dismiss`);
            await fetchData();
        } catch (err) {
            console.error('Dismiss failed:', err);
        }
    };

    const deleteRoutine = async (routineId: string) => {
        try {
            await api.delete(`/routines/${routineId}`);
            await fetchData();
        } catch (err) {
            console.error('Delete failed:', err);
        }
    };

    const generateRoutine = async () => {
        if (!activeReplica || !generatePrompt.trim()) return;
        setGenerating(true);
        try {
            await api.post('/routines/generate', {
                replicaId: activeReplica.id,
                description: generatePrompt.trim(),
            });
            setGeneratePrompt('');
            setShowCreateModal(false);
            await fetchData();
        } catch (err) {
            console.error('Generate failed:', err);
        } finally {
            setGenerating(false);
        }
    };

    // ── Stats ─────────────────────────────────────────────
    const stats = useMemo(() => {
        const total = todayPlan.length;
        const completed = todayPlan.filter(r => r.completion?.status === 'completed').length;
        const totalSteps = todayPlan.reduce((sum, r) => sum + r.steps.length, 0);
        const completedSteps = todayPlan.reduce((sum, r) => sum + (r.completion?.stepsCompleted || 0), 0);
        const maxStreak = Math.max(0, ...todayPlan.map(r => r.streakCount));
        const avgCompletion = allRoutines.length > 0
            ? allRoutines.reduce((sum, r) => sum + r.completionRate, 0) / allRoutines.length
            : 0;
        return { total, completed, totalSteps, completedSteps, maxStreak, avgCompletion };
    }, [todayPlan, allRoutines]);

    // ── Render helpers ────────────────────────────────────
    const getIcon = (name: string, props?: any) => {
        const IconComp = ICON_MAP[name] || Sparkles;
        return <IconComp {...props} />;
    };

    const getColor = (name: string) => COLOR_MAP[name] || COLOR_MAP.blue;

    // ── Loading state ─────────────────────────────────────
    if (loading) {
        return (
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                minHeight: '60vh', gap: 12,
            }}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-text-tertiary)' }} />
                <span style={{ color: 'var(--color-text-tertiary)', fontSize: 14 }}>Loading your routines...</span>
            </div>
        );
    }

    // ── No replica ────────────────────────────────────────
    if (!activeReplica) {
        return (
            <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                minHeight: '60vh', gap: 16, padding: 40,
            }}>
                <Sparkles size={48} style={{ color: 'var(--color-text-quaternary)' }} />
                <p style={{ color: 'var(--color-text-tertiary)', fontSize: 15 }}>
                    Create a Replica first to set up daily routines.
                </p>
                <button
                    onClick={() => onNavigate('create-replica')}
                    style={{
                        padding: '10px 24px', borderRadius: 12, border: 'none', cursor: 'pointer',
                        background: 'var(--color-accent)', color: '#fff', fontWeight: 600, fontSize: 14,
                    }}
                >
                    Create Replica
                </button>
            </div>
        );
    }

    return (
        <div style={{ padding: '24px 32px', maxWidth: 960, margin: '0 auto' }}>

            {/* ━━━ HEADER ━━━ */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                        <div style={{
                            width: 36, height: 36, borderRadius: 10,
                            background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <Sun size={20} color="#fff" />
                        </div>
                        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text-primary)', margin: 0 }}>
                            Daily Routines
                        </h1>
                    </div>
                    <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)', margin: 0, marginLeft: 46 }}>
                        {todayDate && new Date(todayDate + 'T12:00:00').toLocaleDateString('en-US', {
                            weekday: 'long', month: 'long', day: 'numeric',
                        })}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button
                        onClick={() => onNavigate('work')}
                        style={{
                            padding: '8px 12px', borderRadius: 10, border: '1px solid rgba(99,102,241,0.25)',
                            background: 'rgba(99,102,241,0.08)', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
                            color: '#6366f1', fontWeight: 600,
                        }}
                    >
                        <Zap size={14} /> Mandates
                    </button>
                    <button
                        onClick={() => onNavigate('work-mode')}
                        style={{
                            padding: '8px 12px', borderRadius: 10, border: '1px solid rgba(139,92,246,0.25)',
                            background: 'rgba(139,92,246,0.08)', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
                            color: '#8b5cf6', fontWeight: 600,
                        }}
                    >
                        <Target size={14} /> Work Mode
                    </button>
                    <button
                        onClick={() => fetchData()}
                        style={{
                            padding: '8px 12px', borderRadius: 10, border: '1px solid var(--color-border)',
                            background: 'var(--color-bg-secondary)', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
                            color: 'var(--color-text-secondary)',
                        }}
                    >
                        <RefreshCw size={14} /> Refresh
                    </button>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        style={{
                            padding: '8px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
                            background: 'var(--color-accent)', color: '#fff', fontWeight: 600, fontSize: 13,
                            display: 'flex', alignItems: 'center', gap: 6,
                            boxShadow: '0 2px 8px rgba(59,130,246,0.25)',
                        }}
                    >
                        <Plus size={14} /> New Routine
                    </button>
                </div>
            </div>

            {/* ━━━ STATS BAR ━━━ */}
            <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24,
            }}>
                {[
                    { label: 'Today', value: `${stats.completed}/${stats.total}`, icon: <Calendar size={16} />, color: '#3b82f6' },
                    { label: 'Steps Done', value: `${stats.completedSteps}/${stats.totalSteps}`, icon: <CheckCircle2 size={16} />, color: '#10b981' },
                    { label: 'Best Streak', value: `${stats.maxStreak}`, icon: <Flame size={16} />, color: '#f59e0b' },
                    { label: 'Avg Rate', value: `${Math.round(stats.avgCompletion * 100)}%`, icon: <Target size={16} />, color: '#8b5cf6' },
                ].map(({ label, value, icon, color }) => (
                    <div key={label} style={{
                        padding: '14px 16px', borderRadius: 14,
                        background: 'var(--color-bg-secondary)',
                        border: '1px solid var(--color-border)',
                        display: 'flex', alignItems: 'center', gap: 12,
                    }}>
                        <div style={{
                            width: 32, height: 32, borderRadius: 8,
                            background: `${color}15`, display: 'flex',
                            alignItems: 'center', justifyContent: 'center', color,
                        }}>
                            {icon}
                        </div>
                        <div>
                            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text-primary)' }}>{value}</div>
                            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* ━━━ PROPOSAL BANNER ━━━ */}
            {proposals.length > 0 && (
                <div style={{
                    padding: '16px 20px', borderRadius: 14, marginBottom: 20,
                    background: 'linear-gradient(135deg, rgba(139,92,246,0.08), rgba(59,130,246,0.08))',
                    border: '1px solid rgba(139,92,246,0.2)',
                    display: 'flex', alignItems: 'center', gap: 14,
                }}>
                    <div style={{
                        width: 40, height: 40, borderRadius: 10,
                        background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        <Sparkles size={20} color="#fff" />
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 2 }}>
                            {activeReplica.name} suggested a new routine
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                            {proposals[0].proposalNote || `"${proposals[0].name}" — ${proposals[0].description}`}
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                        <button
                            onClick={() => acceptProposal(proposals[0].id)}
                            style={{
                                padding: '6px 14px', borderRadius: 8, border: 'none',
                                background: '#8b5cf6', color: '#fff', fontWeight: 600,
                                fontSize: 12, cursor: 'pointer',
                            }}
                        >Accept</button>
                        <button
                            onClick={() => dismissProposal(proposals[0].id)}
                            style={{
                                padding: '6px 14px', borderRadius: 8,
                                border: '1px solid var(--color-border)', background: 'transparent',
                                color: 'var(--color-text-tertiary)', fontSize: 12, cursor: 'pointer',
                            }}
                        >Dismiss</button>
                    </div>
                </div>
            )}

            {/* ━━━ TAB BAR ━━━ */}
            <div style={{
                display: 'flex', gap: 4, marginBottom: 20, padding: 4, borderRadius: 12,
                background: 'var(--color-bg-tertiary)',
            }}>
                {[
                    { key: 'today' as const, label: "Today's Plan", icon: <Sun size={14} /> },
                    { key: 'all' as const, label: 'All Routines', icon: <Calendar size={14} /> },
                ].map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        style={{
                            flex: 1, padding: '8px 16px', borderRadius: 8, border: 'none',
                            cursor: 'pointer', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', gap: 6, fontSize: 13, fontWeight: 600,
                            background: activeTab === tab.key ? 'var(--color-bg-primary)' : 'transparent',
                            color: activeTab === tab.key ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)',
                            boxShadow: activeTab === tab.key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                            transition: 'all 0.2s ease',
                        }}
                    >
                        {tab.icon} {tab.label}
                    </button>
                ))}
            </div>

            {/* ━━━ TODAY'S PLAN ━━━ */}
            {activeTab === 'today' && (
                <div>
                    {todayPlan.length === 0 ? (
                        <div style={{
                            textAlign: 'center', padding: '48px 24px', borderRadius: 16,
                            background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
                        }}>
                            <Sun size={40} style={{ color: 'var(--color-text-quaternary)', marginBottom: 12 }} />
                            <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 6 }}>
                                No routines for today
                            </h3>
                            <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 16 }}>
                                Let your replica design a personalized routine for you
                            </p>
                            <button
                                onClick={() => setShowCreateModal(true)}
                                style={{
                                    padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer',
                                    background: 'var(--color-accent)', color: '#fff', fontWeight: 600, fontSize: 13,
                                }}
                            >
                                <Plus size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
                                Create First Routine
                            </button>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            {todayPlan.map(routine => {
                                const color = getColor(routine.color);
                                const isCompleted = routine.completion?.status === 'completed';
                                const isSkipped = routine.completion?.status === 'skipped';
                                const stepsCompleted = routine.completion?.stepsCompleted || 0;
                                const stepsTotal = routine.steps.length;
                                const progress = stepsTotal > 0 ? stepsCompleted / stepsTotal : 0;

                                return (
                                    <div
                                        key={routine.routineId}
                                        style={{
                                            borderRadius: 16, overflow: 'hidden',
                                            background: 'var(--color-bg-secondary)',
                                            border: `1px solid ${isCompleted ? 'rgba(16,185,129,0.3)' : color.border}`,
                                            boxShadow: isCompleted ? '0 0 16px rgba(16,185,129,0.08)' : color.glow,
                                            transition: 'all 0.3s ease',
                                            opacity: isSkipped ? 0.5 : 1,
                                        }}
                                    >
                                        {/* Header */}
                                        <div style={{
                                            padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14,
                                            borderBottom: `1px solid var(--color-border)`,
                                        }}>
                                            <div style={{
                                                width: 40, height: 40, borderRadius: 12,
                                                background: color.gradient,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                flexShrink: 0,
                                            }}>
                                                {getIcon(routine.icon, { size: 20, color: '#fff' })}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{
                                                    display: 'flex', alignItems: 'center', gap: 8,
                                                }}>
                                                    <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                                                        {routine.name}
                                                    </span>
                                                    {isCompleted && (
                                                        <span style={{
                                                            fontSize: 10, fontWeight: 700, padding: '2px 8px',
                                                            borderRadius: 6, background: 'rgba(16,185,129,0.12)',
                                                            color: '#10b981', textTransform: 'uppercase',
                                                        }}>
                                                            Done
                                                        </span>
                                                    )}
                                                    {isSkipped && (
                                                        <span style={{
                                                            fontSize: 10, fontWeight: 700, padding: '2px 8px',
                                                            borderRadius: 6, background: 'rgba(161,161,170,0.12)',
                                                            color: '#a1a1aa', textTransform: 'uppercase',
                                                        }}>
                                                            Skipped
                                                        </span>
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 2 }}>
                                                    {routine.timeSlot && (
                                                        <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                                                            <Clock size={11} style={{ verticalAlign: -1, marginRight: 3 }} />
                                                            {routine.timeSlot}
                                                        </span>
                                                    )}
                                                    {routine.duration && (
                                                        <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                                                            ~{routine.duration}m
                                                        </span>
                                                    )}
                                                    {routine.streakCount > 0 && (
                                                        <span style={{ fontSize: 12, color: '#f59e0b', fontWeight: 600 }}>
                                                            <Flame size={12} style={{ verticalAlign: -2, marginRight: 2 }} />
                                                            {routine.streakCount}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Progress ring */}
                                            <div style={{ position: 'relative', width: 44, height: 44 }}>
                                                <svg width="44" height="44" viewBox="0 0 44 44">
                                                    <circle cx="22" cy="22" r="18" fill="none"
                                                        stroke="var(--color-border)" strokeWidth="3" />
                                                    <circle cx="22" cy="22" r="18" fill="none"
                                                        stroke={isCompleted ? '#10b981' : color.text}
                                                        strokeWidth="3" strokeLinecap="round"
                                                        strokeDasharray={`${progress * 113.1} 113.1`}
                                                        transform="rotate(-90 22 22)"
                                                        style={{ transition: 'stroke-dasharray 0.5s ease' }}
                                                    />
                                                </svg>
                                                <span style={{
                                                    position: 'absolute', inset: 0, display: 'flex',
                                                    alignItems: 'center', justifyContent: 'center',
                                                    fontSize: 11, fontWeight: 700,
                                                    color: isCompleted ? '#10b981' : 'var(--color-text-secondary)',
                                                }}>
                                                    {stepsCompleted}/{stepsTotal}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Steps */}
                                        {!isSkipped && (
                                            <div style={{ padding: '12px 20px' }}>
                                                {routine.steps.map((step, idx) => {
                                                    const stepResult = routine.completion?.stepResults?.[idx];
                                                    const isStepDone = stepResult?.completed || isCompleted;
                                                    const isToggling = togglingStep === `${routine.routineId}-${idx}`;

                                                    return (
                                                        <button
                                                            key={step.id}
                                                            onClick={() => !isCompleted && !isSkipped && toggleStep(routine.routineId, idx)}
                                                            disabled={isCompleted || isSkipped || isToggling}
                                                            style={{
                                                                display: 'flex', alignItems: 'center', gap: 10,
                                                                width: '100%', padding: '8px 0',
                                                                background: 'none', border: 'none', cursor: isCompleted ? 'default' : 'pointer',
                                                                textAlign: 'left', borderBottom: idx < routine.steps.length - 1 ? '1px solid var(--color-border)' : 'none',
                                                            }}
                                                        >
                                                            {isToggling ? (
                                                                <Loader2 size={18} style={{ animation: 'spin 1s linear infinite', color: color.text, flexShrink: 0 }} />
                                                            ) : isStepDone ? (
                                                                <CheckCircle2 size={18} style={{ color: '#10b981', flexShrink: 0 }} />
                                                            ) : (
                                                                <Circle size={18} style={{ color: 'var(--color-text-quaternary)', flexShrink: 0 }} />
                                                            )}
                                                            <div style={{ flex: 1 }}>
                                                                <span style={{
                                                                    fontSize: 13, fontWeight: 500,
                                                                    color: isStepDone ? 'var(--color-text-tertiary)' : 'var(--color-text-primary)',
                                                                    textDecoration: isStepDone ? 'line-through' : 'none',
                                                                }}>
                                                                    {step.title}
                                                                </span>
                                                                {step.description && (
                                                                    <span style={{ fontSize: 11, color: 'var(--color-text-quaternary)', marginLeft: 6 }}>
                                                                        — {step.description}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {step.type === 'workflow' && (
                                                                <span style={{
                                                                    fontSize: 10, fontWeight: 600, padding: '1px 6px',
                                                                    borderRadius: 4, background: `${color.text}15`, color: color.text,
                                                                }}>
                                                                    <Zap size={9} style={{ verticalAlign: -1, marginRight: 2 }} />
                                                                    Auto
                                                                </span>
                                                            )}
                                                            {step.duration && (
                                                                <span style={{ fontSize: 11, color: 'var(--color-text-quaternary)' }}>
                                                                    {step.duration}m
                                                                </span>
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Footer actions */}
                                        {!isCompleted && !isSkipped && (
                                            <div style={{
                                                padding: '10px 20px', display: 'flex', gap: 8,
                                                borderTop: '1px solid var(--color-border)',
                                            }}>
                                                <button
                                                    onClick={() => completeRoutine(routine.routineId)}
                                                    style={{
                                                        flex: 1, padding: '7px 12px', borderRadius: 8, border: 'none',
                                                        cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                                        background: 'rgba(16,185,129,0.1)', color: '#10b981',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                                                    }}
                                                >
                                                    <Check size={13} /> Complete All
                                                </button>
                                                <button
                                                    onClick={() => skipRoutine(routine.routineId)}
                                                    style={{
                                                        padding: '7px 12px', borderRadius: 8,
                                                        border: '1px solid var(--color-border)', cursor: 'pointer',
                                                        fontSize: 12, fontWeight: 500, background: 'transparent',
                                                        color: 'var(--color-text-tertiary)',
                                                        display: 'flex', alignItems: 'center', gap: 5,
                                                    }}
                                                >
                                                    <SkipForward size={13} /> Skip
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ━━━ ALL ROUTINES ━━━ */}
            {activeTab === 'all' && (
                <div>
                    {allRoutines.filter(r => !r.isProposed).length === 0 ? (
                        <div style={{
                            textAlign: 'center', padding: '48px 24px', borderRadius: 16,
                            background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
                        }}>
                            <Calendar size={40} style={{ color: 'var(--color-text-quaternary)', marginBottom: 12 }} />
                            <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 6 }}>
                                No routines yet
                            </h3>
                            <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                                Create your first routine to get started
                            </p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {allRoutines.filter(r => !r.isProposed).map(routine => {
                                const color = getColor(routine.color);
                                return (
                                    <div
                                        key={routine.id}
                                        style={{
                                            padding: '14px 18px', borderRadius: 14,
                                            background: 'var(--color-bg-secondary)',
                                            border: `1px solid var(--color-border)`,
                                            display: 'flex', alignItems: 'center', gap: 14,
                                        }}
                                    >
                                        <div style={{
                                            width: 36, height: 36, borderRadius: 10,
                                            background: color.gradient,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                        }}>
                                            {getIcon(routine.icon, { size: 18, color: '#fff' })}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                                                {routine.name}
                                            </div>
                                            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', display: 'flex', gap: 10, marginTop: 2 }}>
                                                <span>{FREQ_LABELS[routine.frequency] || routine.frequency}</span>
                                                {routine.timeSlot && <span>at {routine.timeSlot}</span>}
                                                <span>{routine.steps.length} steps</span>
                                                {routine.streakCount > 0 && (
                                                    <span style={{ color: '#f59e0b' }}>
                                                        <Flame size={11} style={{ verticalAlign: -1 }} /> {routine.streakCount}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            {/* Completion rate mini bar */}
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text-primary)' }}>
                                                    {Math.round(routine.completionRate * 100)}%
                                                </span>
                                                <div style={{
                                                    width: 48, height: 4, borderRadius: 2,
                                                    background: 'var(--color-border)', overflow: 'hidden', marginTop: 2,
                                                }}>
                                                    <div style={{
                                                        width: `${routine.completionRate * 100}%`, height: '100%',
                                                        borderRadius: 2, background: color.gradient,
                                                        transition: 'width 0.5s ease',
                                                    }} />
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => deleteRoutine(routine.id)}
                                                style={{
                                                    padding: 6, borderRadius: 6, border: 'none',
                                                    background: 'transparent', cursor: 'pointer',
                                                    color: 'var(--color-text-quaternary)',
                                                }}
                                                title="Delete routine"
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ━━━ CREATE MODAL ━━━ */}
            {showCreateModal && (
                <div
                    style={{
                        position: 'fixed', inset: 0, zIndex: 1000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                    onClick={(e) => { if (e.target === e.currentTarget) setShowCreateModal(false); }}
                >
                    <div style={{
                        position: 'absolute', inset: 0,
                        background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)',
                    }} />
                    <div style={{
                        position: 'relative', zIndex: 1, width: '100%', maxWidth: 500,
                        padding: '28px', borderRadius: 20,
                        background: 'var(--color-bg-primary)',
                        border: '1px solid var(--color-border)',
                        boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{
                                    width: 36, height: 36, borderRadius: 10,
                                    background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <Sparkles size={18} color="#fff" />
                                </div>
                                <div>
                                    <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--color-text-primary)', margin: 0 }}>
                                        Create Routine
                                    </h2>
                                    <p style={{ fontSize: 12, color: 'var(--color-text-tertiary)', margin: 0 }}>
                                        Describe it — {activeReplica.name} will design it for you
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowCreateModal(false)}
                                style={{
                                    padding: 6, borderRadius: 8, border: 'none',
                                    background: 'var(--color-bg-tertiary)', cursor: 'pointer',
                                    color: 'var(--color-text-tertiary)',
                                }}
                            >
                                <X size={16} />
                            </button>
                        </div>

                        <textarea
                            value={generatePrompt}
                            onChange={(e) => setGeneratePrompt(e.target.value)}
                            placeholder={'e.g. "A morning routine that helps me start the day focused with email review, planning, and a quick mindfulness break"'}
                            rows={4}
                            style={{
                                width: '100%', padding: '14px 16px', borderRadius: 12, resize: 'vertical',
                                border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
                                color: 'var(--color-text-primary)', fontSize: 14, fontFamily: 'inherit',
                                outline: 'none', lineHeight: 1.5, boxSizing: 'border-box',
                            }}
                            autoFocus
                        />

                        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                            {['Morning focus routine', 'Evening wind-down', 'Weekly review', 'Work productivity flow'].map(suggestion => (
                                <button
                                    key={suggestion}
                                    onClick={() => setGeneratePrompt(suggestion)}
                                    style={{
                                        padding: '5px 10px', borderRadius: 8, fontSize: 11, fontWeight: 500,
                                        border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
                                        color: 'var(--color-text-tertiary)', cursor: 'pointer',
                                    }}
                                >
                                    {suggestion}
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={generateRoutine}
                            disabled={generating || !generatePrompt.trim()}
                            style={{
                                width: '100%', marginTop: 16, padding: '12px 20px', borderRadius: 12,
                                border: 'none', cursor: generating || !generatePrompt.trim() ? 'not-allowed' : 'pointer',
                                background: generating || !generatePrompt.trim() ? 'var(--color-bg-tertiary)' : 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
                                color: '#fff', fontWeight: 600, fontSize: 14,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                boxShadow: generating || !generatePrompt.trim() ? 'none' : '0 4px 12px rgba(99,102,241,0.3)',
                            }}
                        >
                            {generating ? (
                                <>
                                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                                    {activeReplica.name} is designing your routine...
                                </>
                            ) : (
                                <>
                                    <Sparkles size={16} />
                                    Generate with {activeReplica.name}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* ━━━ GLOBAL STYLES ━━━ */}
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};

export default DailyRoutinePage;
